/**
 * Composable for fetching and managing enum values from Supabase
 * Provides reactive access to database enum types
 */
import { ref, onMounted } from 'vue'
import { supabase } from '@/services/supabase'

/**
 * Interface for email category enum values
 */
export interface IEmailCategory {
  /** The enum value as stored in database */
  value: string
  /** Human-readable label for display */
  label: string
}

/**
 * Composable for managing EMAIL_CATEGORY enum values
 * Fetches valid category values from Supabase enum type
 * 
 * @returns Object containing enum values and loading state
 */
export function useEmailCategories() {
  /** Reactive array of email category enum values */
  const emailCategories = ref<IEmailCategory[]>([])
  
  /** Loading state indicator */
  const isLoading = ref(false)
  
  /** Error state for enum fetching */
  const error = ref<string | null>(null)

  /**
   * Fetches EMAIL_CATEGORY enum values from Supabase
   * Uses PostgreSQL system tables to get enum values
   */
  const fetchEmailCategories = async (): Promise<void> => {
    isLoading.value = true
    error.value = null
    
    try {
      // Query PostgreSQL system tables to get enum values
      const { data, error: fetchError } = await supabase
        .rpc('get_enum_values', { enum_name: 'EMAIL_CATEGORY' })
      
      if (fetchError) {
        console.error('Error fetching email categories:', fetchError)
        error.value = 'Failed to fetch email categories'
        // Fallback to hardcoded values if database query fails
        emailCategories.value = [
          { value: 'MAIL_TO_PLANNERS_START', label: 'Mail to Planners Start' },
          { value: 'MONTHLY_REPORT_NOTIFICATION', label: 'Monthly Report Notification' },
          { value: 'ALERT_NOTIFICATION', label: 'Alert Notification' },
          { value: 'MANAGEMENT_BOARD_MEETING', label: 'Management Board Meeting' },
          { value: 'FORECAST_NOTIFICATION', label: 'Forecast Notification' }
        ]
        return
      }

      // Transform enum values to display format
      emailCategories.value = data?.map((enumValue: string) => ({
        value: enumValue,
        label: formatEnumLabel(enumValue)
      })) || []
      
    } catch (err) {
      console.error('Unexpected error fetching email categories:', err)
      error.value = 'Unexpected error occurred'
      // Fallback to hardcoded values
      emailCategories.value = [
        { value: 'MAIL_TO_PLANNERS_START', label: 'Mail to Planners Start' },
        { value: 'MONTHLY_REPORT_NOTIFICATION', label: 'Monthly Report Notification' },
        { value: 'ALERT_NOTIFICATION', label: 'Alert Notification' },
        { value: 'MANAGEMENT_BOARD_MEETING', label: 'Management Board Meeting' },
        { value: 'FORECAST_NOTIFICATION', label: 'Forecast Notification' }
      ]
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Formats enum value to human-readable label
   * Converts SNAKE_CASE to Title Case
   * 
   * @param enumValue - The enum value to format
   * @returns Formatted label string
   */
  const formatEnumLabel = (enumValue: string): string => {
    return enumValue
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  /**
   * Refreshes the enum values from database
   */
  const refresh = (): Promise<void> => {
    return fetchEmailCategories()
  }

  // Fetch enum values on component mount
  onMounted(() => {
    fetchEmailCategories()
  })

  return {
    emailCategories,
    isLoading,
    error,
    refresh
  }
}